/*!
 * The Tiles House — AI Tile Advisor widget, v3 (thin client)
 * Build: 2026-09-27-v3.2-advise
 *
 * Drop-in replacement for tth-advisor.js (v26). Same public API:
 *   window.TTHAdvisor.init({ mode, shopDomain, messagesId, inputId, sendBtnId,
 *                            chipsId, panelId, containerSelector })
 *
 * All intent extraction + tile filtering now happens in the Cloudflare
 * Worker's /advise endpoint. This file only sends the conversation and
 * renders what comes back. No catalog logic lives in the browser.
 */
(function () {
  "use strict";
  try { console.log("TTH Tile Advisor build: 2026-09-27-v3.2-advise"); } catch (e) {}

  var WORKER_BASE = "https://rough-bush-d0a3.inboxshubham2.workers.dev";
  var ADVISE_URL = WORKER_BASE + "/advise";
  var CLICK_URL = WORKER_BASE + "/log-click";
  var SID_KEY = "tth_advisor_sid";
  var MAX_EXCLUDE = 150;
  var STOREFRONT_TOKEN = "150dd526cb53fa17928fca8d4b4cc58e";
  var MAX_HISTORY = 20;
  var REQUEST_TIMEOUT_MS = 45000;
  var VISUALIZER_ALLOWED_ORIGIN = "https://thetileshouse.com";
  var VISUALIZER_PAGE_URL = "https://thetileshouse.com/pages/visualizer";
  var SESSION_KEY = "tth_advisor_session";

  var BLOCK_LABELS = {
    floor: "Floor Elements",
    wall: "Wall Canvases",
    highlighter: "Accent & Highlighter Tiles",
    coordinate: "Pairs Well With"
  };

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  // Safe text -> HTML: escape, then allow **bold** and line breaks only.
  // (No single-* italics: tile sizes like 24*24 would get mangled.)
  function formatSpeech(text) {
    return esc(text)
      .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br>");
  }

  function safePath(url) {
    // Only allow same-site relative paths or thetileshouse.com links.
    var u = String(url || "");
    if (/^\/[^/]/.test(u)) return u;
    if (/^https:\/\/(www\.)?thetileshouse\.com\//.test(u)) return u;
    return "";
  }

  // Stable per-browser id so chat logs and click logs can be joined (the
  // worker's logAdvise/logClickEvent already expect it).
  function getSessionId() {
    try {
      var sid = localStorage.getItem(SID_KEY);
      if (!sid) {
        sid = "s" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
        localStorage.setItem(SID_KEY, sid);
      }
      return sid;
    } catch (e) { return "anon"; }
  }

  function mfVal(mf) { return mf && mf.value ? String(mf.value).toLowerCase().trim() : ""; }

  window.TTHAdvisor = {
    init: function (opts) {
      opts = opts || {};
      var shopDomain = opts.shopDomain || "thetilehouse.myshopify.com";
      var mode = opts.mode || "widget";
      var linkTarget = mode === "widget" ? ' target="_blank" rel="noopener"' : "";
      var inputMaxHeight = mode === "widget" ? 80 : 100;
      var messages = document.getElementById(opts.messagesId);
      var input = document.getElementById(opts.inputId);
      var sendBtn = document.getElementById(opts.sendBtnId);
      var chips = document.getElementById(opts.chipsId);
      var persist = mode === "page";
      var embedded = /(^|[?&])embedded=1(&|$)/.test(location.search);

      if (!messages || !input || !sendBtn) {
        try { console.warn("[tth-advisor] missing DOM elements; init aborted"); } catch (e) {}
        return { focusInput: function () {}, clearSession: function () {} };
      }

      var conversationHistory = [];   // [{role, content}] — content is plain text
      var visualizerContext = "";     // extra context sent with every request (embedded mode)
      var busy = false;
      var shownHandles = [];         // every tile shown this session — sent as excludeHandles so "more" gives fresh tiles
      var sessionId = getSessionId();

      /* ---------------- session (page mode) ---------------- */
      function saveSession() {
        if (!persist) return;
        try {
          localStorage.setItem(SESSION_KEY, JSON.stringify({
            v: 3, html: messages.innerHTML, history: conversationHistory, shown: shownHandles
          }));
        } catch (e) {}
      }
      function restoreSession() {
        if (!persist) return false;
        try {
          var saved = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
          // v26 sessions stored raw JSON replies as assistant content — don't reuse those.
          if (!saved || saved.v !== 3 || !saved.html || !saved.history || !saved.history.length) return false;
          messages.innerHTML = saved.html;
          conversationHistory = saved.history;
          if (Array.isArray(saved.shown)) shownHandles = saved.shown;
          if (chips) chips.style.display = "none";
          messages.scrollTop = messages.scrollHeight;
          return true;
        } catch (e) { return false; }
      }

      /* ---------------- DOM helpers ---------------- */
      function addMessage(html, type) {
        var div = document.createElement("div");
        div.className = "tth-msg tth-msg-" + type;
        if (type === "user") div.textContent = html; else div.innerHTML = html;
        messages.appendChild(div);
        messages.scrollTop = messages.scrollHeight;
        return div;
      }
      function addTyping() {
        var div = document.createElement("div");
        div.className = "tth-msg tth-msg-ai tth-typing";
        div.id = "tth-typing";
        div.innerHTML = "<span></span><span></span><span></span>";
        messages.appendChild(div);
        messages.scrollTop = messages.scrollHeight;
      }
      function removeTyping() {
        var t = document.getElementById("tth-typing");
        if (t) t.remove();
      }

      /* ---------------- rendering ---------------- */
      function priceLabel(p) {
        var per = parseFloat(p.price_per_sqft != null ? p.price_per_sqft : p._price);
        if (!isNaN(per) && per > 0) return "\u20B9" + Math.round(per) + "/sqft";
        var box = parseFloat(p.box_price);
        if (!isNaN(box) && box > 0 && /piece|pc|sheet|roll/i.test(String(p.priced_by || ""))) {
          return "\u20B9" + Math.round(box) + "/" + String(p.priced_by).replace(/^\s*(per\s+)?/i, "").replace(/\.$/, "");
        }
        return "";
      }

      function buildProductCard(p, section) {
        var title = esc(p.title);
        var handle = String(p.handle || "");
        var img = '<div style="width:60px;height:60px;background:#f4f4f4;border-radius:6px;flex-shrink:0;"></div>';
        if (p.image_url) {
          var url = String(p.image_url);
          url += (url.indexOf("?") !== -1 ? "&" : "?") + "width=120";
          img = '<img src="' + esc(url) + '" loading="lazy" decoding="async" style="width:60px;height:60px;object-fit:cover;border-radius:6px;flex-shrink:0;" alt="' + title + '">';
        }
        var price = priceLabel(p);
        var priceHtml = price ? '<p style="font-size:11px;color:#1a1a1a;font-weight:600;margin:2px 0 3px;">' + esc(price) + "</p>" : "";
        var actions = '<a href="/products/' + encodeURIComponent(handle) + '"' + linkTarget +
          ' class="tth-view-link" data-handle="' + esc(handle) + '" data-section="' + esc(section || "") + '"' +
          ' style="font-size:11px;color:#ff6b00;font-weight:500;text-decoration:none;">View Product \u2192</a>';
        if (embedded && window.opener) {
          actions = '<a href="#" class="tth-visualize-btn" data-handle="' + esc(handle) +
            '" style="font-size:11px;color:#1a1a1a;font-weight:700;text-decoration:none;margin-right:10px;">Visualize this \u2192</a>' + actions;
        }
        return '<div class="tth-product-card" style="display:flex;gap:10px;align-items:center;margin-bottom:8px;">' + img +
          '<div style="flex:1;min-width:0;"><p class="tth-product-card-title" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:13px;margin:0;color:#1a1a1a;">' +
          title + "</p>" + priceHtml + actions + "</div></div>";
      }

      function buildLabel(text, first) {
        return '<div class="tth-option-label" style="font-weight:600;margin-top:' + (first ? 10 : 14) +
          'px;margin-bottom:6px;color:#555;font-size:12px;text-transform:uppercase;">' + esc(text) + "</div>";
      }

      function buildBrowseLink(url, label) {
        var href = safePath(url);
        if (!href) return "";
        return '<div style="margin-top:10px;margin-bottom:15px;"><a href="' + esc(href) + '"' + linkTarget +
          ' style="font-size:12px;font-weight:600;color:#1a1a1a;text-decoration:none;border-bottom:1.5px solid #ff6b00;padding-bottom:2px;">' +
          esc(label) + " \u2192</a></div>";
      }

      function productsOf(v) {
        if (Array.isArray(v)) return v;
        if (v && Array.isArray(v.products)) return v.products;
        if (v && Array.isArray(v.tiles)) return v.tiles;
        return [];
      }

      var lastShown = [];
      function sameAsLast(now) {
        if (!now.length || now.length !== lastShown.length) return false;
        for (var k = 0; k < now.length; k++) if (lastShown.indexOf(now[k]) === -1) return false;
        return true;
      }

      function renderReply(data, userText) {
        var shownNow = [];
        var html = '<div class="tth-salesman-speech" style="line-height:1.5;color:#222;font-size:14px;margin-bottom:12px;">' +
          formatSpeech(data.reply || "Here are some options for you:") + "</div>";
        if (data.note) {
          html += '<div style="font-size:11.5px;color:#a06a2c;margin-bottom:6px;">' + esc(data.note) + "</div>";
        }
        var browse = data.browse || {};
        var first = true;
        var seen = {};
        // Accent/highlighter block only when the customer is actually after accents
        // (backsplash / highlighter mode) — otherwise it repeats ordinary tiles.
        var showAccents = data.mode === "highlighter" || data.backsplash === true;
        ["floor", "wall", "highlighter", "coordinate"].forEach(function (key) {
          if (key === "highlighter" && !showAccents) return;
          var list = productsOf(data[key]).filter(function (p) {
            if (!p || !p.handle || !p.title || seen[p.handle]) return false;
            seen[p.handle] = true;
            shownNow.push(p.handle);
            return true;
          });
          if (!list.length) return;
          var label = BLOCK_LABELS[key];
          if (key === "coordinate" && data.coordinate && data.coordinate.color) {
            label = "Pairs well: " + data.coordinate.color + " " + (data.coordinate.surface || "") + " tiles";
          }
          html += buildLabel(label, first);
          first = false;
          list.forEach(function (p) { html += buildProductCard(p, key); });
          if (key === "floor") html += buildBrowseLink(browse.floor, "Browse more floor designs");
          if (key === "wall") html += buildBrowseLink(browse.wall, "Browse more wall designs");
        });
        // Browse links for blocks that returned no cards but still have a collection
        if (!productsOf(data.floor).length && browse.floor && !first) html += buildBrowseLink(browse.floor, "Browse more floor designs");
        if (!productsOf(data.wall).length && browse.wall) html += buildBrowseLink(browse.wall, "Browse more wall designs");

        // No tiles at all, but the customer was asking for tiles: never leave a dead end.
        if (first && data.mode && data.mode !== "none") {
          html += buildSearchFallback(data, "Browse matching tiles in our catalogue");
        }
        // A follow-up ("more", "cheaper", "sage please"...) that returned exactly the same tiles
        // as last time: say so and point to the full range instead of silently repeating.
        else if (!first && sameAsLast(shownNow)) {
          html += '<div style="font-size:11.5px;color:#a06a2c;margin:6px 0;">These are our closest matches — see the full range for more choice.</div>' +
            buildSearchFallback(data, "See the full range");
        }
        lastShown = shownNow;
        shownNow.forEach(function (h) { if (shownHandles.indexOf(h) === -1) shownHandles.push(h); });
        if (shownHandles.length > MAX_EXCLUDE) shownHandles = shownHandles.slice(-MAX_EXCLUDE);
        return html;
      }

      // Shopify storefront search built from what the customer asked for.
      function buildSearchFallback(data, label) {
        var i = data.intent || {};
        var surface = data.mode === "floor" || data.mode === "wall" ? data.mode : (i.surface === "floor" || i.surface === "wall" ? i.surface : "");
        var words = [];
        if (i.colors && i.colors[0]) words.push(i.colors[0]);
        if (i.designs && i.designs[0]) words.push(i.designs[0]);
        if (i.room) words.push(i.room);
        if (surface) words.push(surface);
        words.push("tiles");
        return buildBrowseLink("/search?type=product&q=" + encodeURIComponent(words.join(" ")), label);
      }

      /* ---------------- network ---------------- */
      function payloadMessages() {
        var msgs = conversationHistory.slice(-MAX_HISTORY);
        while (msgs.length && msgs[0].role !== "user") msgs.shift();
        if (visualizerContext && msgs.length) {
          msgs = msgs.slice();
          msgs[0] = { role: "user", content: visualizerContext + "\n\n" + msgs[0].content };
        }
        return msgs;
      }

      async function callAdvise() {
        var ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, REQUEST_TIMEOUT_MS) : null;
        try {
          var res = await fetch(ADVISE_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ messages: payloadMessages(), excludeHandles: shownHandles, session_id: sessionId, mode: mode, embedded: embedded }),
            signal: ctrl ? ctrl.signal : undefined
          });
          var data = await res.json();
          if (!data || typeof data.reply !== "string") throw new Error("Bad /advise response");
          return data;
        } finally {
          if (timer) clearTimeout(timer);
        }
      }

      async function sendMessage() {
        var text = input.value.trim();
        if (!text || busy) return;
        busy = true;
        input.value = "";
        input.style.height = "auto";
        if (chips) chips.style.display = "none";
        addMessage(text, "user");
        addTyping();
        sendBtn.disabled = true;
        conversationHistory.push({ role: "user", content: text });
        try {
          var data = await callAdvise();
          removeTyping();
          if (data.error) { try { console.warn("[tth-advisor] /advise:", data.stage, data.error); } catch (e) {} }
          conversationHistory.push({ role: "assistant", content: data.reply });
          if (conversationHistory.length > MAX_HISTORY) conversationHistory = conversationHistory.slice(-MAX_HISTORY);
          addMessage(renderReply(data, text), "ai");
          saveSession();
        } catch (e) {
          removeTyping();
          conversationHistory.pop(); // drop the unanswered user turn so history stays alternating
          addMessage("Sorry, I couldn\u2019t reach our tile catalogue just now. Please try again in a moment.", "ai");
          try { console.error("[tth-advisor]", e); } catch (e2) {}
        }
        busy = false;
        sendBtn.disabled = false;
        if (mode !== "page") input.focus();
      }

      /* ---------------- click logging (fire-and-forget) ---------------- */
      messages.addEventListener("click", function (e) {
        var a = e.target.closest ? e.target.closest(".tth-view-link, .tth-visualize-btn") : null;
        if (!a) return;
        var card = a.closest(".tth-product-card");
        var view = card ? card.querySelector(".tth-view-link") : a;
        try {
          fetch(CLICK_URL, {
            method: "POST", keepalive: true,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              session_id: sessionId,
              handle: a.getAttribute("data-handle") || "",
              section: (view && view.getAttribute("data-section") || "") + (a.classList.contains("tth-visualize-btn") ? ":visualize" : ""),
              page: location.pathname
            })
          });
        } catch (err) {}
      });

      /* ---------------- visualizer (embedded) mode ---------------- */
      async function fetchPlacedTileDetails(handle) {
        var safeHandle = String(handle || "").replace(/["\\]/g, "");
        if (!safeHandle) return null;
        var gql = JSON.stringify({
          query: '{ productByHandle(handle: "' + safeHandle + '") { title handle mfColor: metafield(namespace: "custom", key: "color") { value } mfDesign: metafield(namespace: "custom", key: "design") { value } mfMaterial: metafield(namespace: "custom", key: "material") { value } mfFinish: metafield(namespace: "custom", key: "finish") { value } } }'
        });
        try {
          var res = await fetch("https://" + shopDomain + "/api/2025-04/graphql.json", {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN },
            body: gql
          });
          var data = await res.json();
          var p = data && data.data && data.data.productByHandle;
          return p ? { title: p.title, color: mfVal(p.mfColor), design: mfVal(p.mfDesign), material: mfVal(p.mfMaterial), finish: mfVal(p.mfFinish) } : null;
        } catch (e) { return null; }
      }
      async function resolvePlacedTiles(snapshot) {
        if (!snapshot || !snapshot.length) return [];
        var details = await Promise.all(snapshot.map(function (t) { return fetchPlacedTileDetails(t.handle); }));
        return snapshot.map(function (t, i) {
          var d = details[i] || {};
          return { handle: t.handle, name: t.name || d.title || t.handle, surfaces: t.surfaces || [],
                   color: d.color || "", design: d.design || "", material: d.material || "", finish: d.finish || "" };
        });
      }
      function describeTile(t) {
        var bits = [];
        if (t.color) bits.push(t.color + " color");
        if (t.design) bits.push(t.design);
        if (t.material) bits.push(t.material);
        if (t.finish) bits.push(t.finish + " finish");
        return t.name + (bits.length ? " (" + bits.join(", ") + ")" : "") +
          (t.surfaces && t.surfaces.length ? " on " + t.surfaces.join(", ") : "");
      }
      function buildVisualizerContext(resolved, room) {
        if ((!resolved || !resolved.length) && !(room && room.label)) return "";
        var s = "[Visualizer context: the customer is designing their room in our 3D Room Visualizer.";
        if (room && room.label) s += " Room: " + room.label + " (use this room unless they say otherwise).";
        if (resolved && resolved.length) s += " Tiles already placed: " + resolved.map(describeTile).join("; ") + ".";
        s += " Use this only when their request relates to it, e.g. matching or 'more like this'.]";
        return s;
      }
      function greetingText(resolved, room) {
        var roomPhrase = room && room.label ? " for your " + room.label.toLowerCase() : "";
        if (!resolved || !resolved.length) {
          return "Hi! I can see you\u2019re working on your room" + roomPhrase + " in the Visualizer. Tell me what you\u2019re looking for \u2014 a matching tile, more options, or anything else \u2014 and I\u2019ll help you find it. \u{1F9F1}";
        }
        var names = resolved.map(function (t) { return t.surfaces && t.surfaces.length ? t.name + " (" + t.surfaces.join(", ") + ")" : t.name; });
        return "Hi! I can see you\u2019ve added " + names.join(" and ") + (roomPhrase ? " to your" + roomPhrase.replace(" for your", "") + " room" : "") +
          " in the Visualizer. Are you looking for something that matches one of these, more options like one of them, or something else entirely? \u{1F9F1}";
      }
      function renderGreeting(resolved, room) {
        messages.innerHTML = "";
        if (chips) chips.style.display = "none";
        addMessage(esc(greetingText(resolved, room)), "ai");
      }

      if (embedded) {
        messages.innerHTML = "";
        if (chips) chips.style.display = "none";
        if (window.opener) {
          var contextTimeout = setTimeout(function () { renderGreeting(null, null); }, 1500);
          window.addEventListener("message", function (e) {
            if (!e.data || typeof e.data !== "object") return;
            if (e.origin !== VISUALIZER_ALLOWED_ORIGIN || e.source !== window.opener || e.data.type !== "tth:context") return;
            clearTimeout(contextTimeout);
            var placed = e.data.placed || [];
            var room = e.data.room || null;
            (async function () {
              var resolved = await resolvePlacedTiles(placed);
              visualizerContext = buildVisualizerContext(resolved, room);
              renderGreeting(resolved, room);
            })();
          });
          try { window.opener.postMessage({ type: "tth:ready" }, VISUALIZER_ALLOWED_ORIGIN); } catch (e) {}
        } else {
          renderGreeting(null, null);
        }
        messages.addEventListener("click", function (e) {
          var btn = e.target.closest ? e.target.closest(".tth-visualize-btn") : null;
          if (!btn) return;
          e.preventDefault();
          var handle = btn.getAttribute("data-handle");
          if (!handle || !window.opener) return;
          try {
            window.opener.location.href = VISUALIZER_PAGE_URL + "?tile=" + encodeURIComponent(handle);
            window.close();
          } catch (err) {}
        });
      }

      /* ---------------- input wiring ---------------- */
      if (chips) {
        chips.addEventListener("click", function (e) {
          if (e.target.classList && e.target.classList.contains("tth-chip")) {
            input.value = e.target.textContent;
            chips.style.display = "none";
            sendMessage();
          }
        });
      }
      sendBtn.addEventListener("click", sendMessage);
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (!sendBtn.disabled) sendMessage();
        }
      });
      input.addEventListener("input", function () {
        this.style.height = "auto";
        this.style.height = Math.min(this.scrollHeight, inputMaxHeight) + "px";
      });

      /* ---------------- mobile / iOS viewport handling (unchanged from v26) ---------------- */
      var isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      if (mode === "page") {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        var container = opts.containerSelector ? document.querySelector(opts.containerSelector) : null;
        if (isIOS && window.visualViewport && container) {
          var vv = window.visualViewport, keyboardOpen = false, rafPending = false;
          var applyViewport = function () {
            if (rafPending) return;
            rafPending = true;
            requestAnimationFrame(function () {
              rafPending = false;
              if (keyboardOpen) {
                container.style.height = vv.height + "px";
                container.style.top = vv.offsetTop + "px";
                messages.scrollTop = messages.scrollHeight;
              }
            });
          };
          var resetViewport = function () { container.style.height = ""; container.style.top = ""; window.scrollTo(0, 0); };
          vv.addEventListener("resize", function () { if (keyboardOpen) applyViewport(); });
          input.addEventListener("focus", function () { keyboardOpen = true; setTimeout(applyViewport, 100); });
          input.addEventListener("blur", function () { keyboardOpen = false; resetViewport(); setTimeout(resetViewport, 300); });
        } else {
          input.addEventListener("focus", function () {
            setTimeout(function () { input.scrollIntoView({ behavior: "smooth", block: "end" }); }, 400);
          });
        }
      }
      if (mode === "widget" && isIOS && opts.panelId) {
        var panel = document.getElementById(opts.panelId);
        if (panel) {
          input.addEventListener("focus", function () {
            setTimeout(function () {
              panel.style.bottom = "0";
              panel.style.maxHeight = "50vh";
              input.scrollIntoView({ behavior: "smooth", block: "end" });
            }, 300);
          });
          input.addEventListener("blur", function () { panel.style.bottom = ""; panel.style.maxHeight = ""; });
        }
      }

      restoreSession();

      return {
        focusInput: function () { input.focus(); },
        clearSession: function () { try { localStorage.removeItem(SESSION_KEY); } catch (e) {} }
      };
    }
  };
})();
